// Miolo do grafo: tipos, cores, física e filtros, portados de
// engine/src/ui/graph.js praticamente linha a linha.
//
// A física, o hash espacial, o vão mínimo das camadas e o `fitView` são
// matemática pura, afinada até o grafo de 237 nós do gateway ficar legível.
// Reescrever isso agora trocaria um layout conhecido por um novo problema, então
// o que muda aqui é só o que o React exige: nada de `document`, nada de global
// mutável de módulo. O estado vira argumento e volta como retorno.
//
// O que NÃO está aqui: desenho (desenho.ts) e eventos (useGrafo.ts).

export type Layout = 'layers' | 'force';
export type Modo = 'codigo' | 'memoria' | 'cobertura';

export interface NoBruto {
  node_key: string;
  kind: string;
  label?: string | null;
  file?: string | null;
  line?: number | null;
  http?: { method: string; path: string } | null;
  signature?: string | null;
  decorators?: string[] | null;
  meta?: {
    handler?: string; basePath?: string; returns?: string; fields?: string[]; loc?: number;
  } | null;
  wt?: boolean;
  review_state?: string | null;
  drift?: boolean;
  orphan?: boolean;
  /** quantos artefatos de memória tocam este nó (modo cobertura) */
  memory?: number;
  author?: string | null;
  status?: string | null;
  events?: number | null;
  unit_key?: string | null;
}

export interface Aresta {
  src: string;
  dst: string;
  rel: string;
  confidence?: string | null;
  /** deriva: a memória cita código que não existe mais */
  broken?: boolean;
}

export interface GrafoBruto {
  nodes?: NoBruto[];
  edges?: Aresta[];
  coverage?: { unit_key: string; memory: number }[];
  totals?: { files?: number; files_with_memory?: number };
}

/** Nó com posição: o que a física move e o desenho lê. */
export interface No extends NoBruto {
  r: number;
  /** largura reservada ao rótulo; é ela que define o vão mínimo na banda */
  lw: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  pinned: boolean;
  seeded: boolean;
}

export interface Camera { x: number; y: number; k: number }
export interface Ponto { x: number; y: number }

export const KIND: Record<string, string> = {
  file: '--brand', controller: '--brand-deep', service: '--ok', module: '--brand-2', class: '--ok',
  method: '--warn', endpoint: '--warn', interface: '--violet', dto: '--violet', type: '--ink-faint',
  function: '--bad', package: '--ink-faint',
  // grafo da memória: o que o TIME sabe, não o que o código faz
  decisao: '--violet', runbook: '--violet', nota: '--brand-2', sessao: '--brand-deep',
  resumo: '--brand', lacuna: '--bad', proposta: '--warn', dono: '--ok', arquivo: '--brand',
  unidade: '--ink-faint', sumiu: '--bad',
};

export const KIND_LABEL: Record<string, string> = {
  file: 'arquivo', controller: 'controller', service: 'service', module: 'módulo', class: 'classe',
  method: 'método', endpoint: 'endpoint', interface: 'interface', dto: 'DTO', type: 'type',
  function: 'função', package: 'pacote',
  decisao: 'decisão', runbook: 'runbook', nota: 'nota do time', sessao: 'sessão',
  resumo: 'resumo de sessão', lacuna: 'lacuna', proposta: 'proposta', dono: 'dono',
  arquivo: 'arquivo', unidade: 'unidade', sumiu: 'sumiu do código',
};

export const REL_LABEL: Record<string, string> = {
  imports: 'importa', 'imports-external': 'importa pacote', defines: 'define', injects: 'injeta',
  exposes: 'expõe', inherits: 'herda', implements: 'implementa', calls: 'chama', uses: 'usa',
  governa: 'governa', anota: 'anota', resumiu: 'resumiu', tocou: 'tocou', originou: 'originou',
  publicou: 'publicou', propoe: 'propõe', aponta: 'aponta para', responde_por: 'responde por',
  deriva: 'deriva (código sumiu)',
};

export const REL_COLOR: Record<string, string> = {
  imports: '--brand', 'imports-external': '--ink-faint', defines: '--ink-faint', injects: '--ok',
  exposes: '--warn', inherits: '--violet', implements: '--violet', calls: '--bad', uses: '--brand-2',
  governa: '--violet', anota: '--brand-2', resumiu: '--brand-deep', tocou: '--brand',
  originou: '--brand-deep', publicou: '--ok', propoe: '--warn', aponta: '--bad',
  responde_por: '--ok', deriva: '--bad',
};

// bandas do layout em camadas: é o caminho que uma requisição percorre
export const BAND: Record<string, number> = {
  endpoint: 0, controller: 1, service: 2, module: 2, class: 3, function: 3, method: 3,
  interface: 4, dto: 4, type: 4, file: 5, package: 6,
  // memória: quem decide em cima, o código embaixo, o que sumiu no fim
  dono: 0, decisao: 1, runbook: 1, sessao: 1, resumo: 2, proposta: 2, nota: 3, lacuna: 3,
  arquivo: 4, unidade: 4, sumiu: 5,
};

export const BAND_NAME = [
  'rotas HTTP', 'controllers', 'serviços e módulos', 'classes e métodos', 'contratos',
  'arquivos', 'pacotes externos',
];

export const DEFAULT_KINDS = [
  'file', 'controller', 'service', 'module', 'class', 'interface', 'dto', 'endpoint', 'package',
];
export const MEMORY_KINDS = [
  'dono', 'decisao', 'runbook', 'sessao', 'resumo', 'proposta', 'nota', 'lacuna', 'arquivo',
  'unidade', 'sumiu',
];

export const ALTURA_DA_BANDA = 145;

const nos = (g: GrafoBruto | null): NoBruto[] => g?.nodes || [];
const arestas = (g: GrafoBruto | null): Aresta[] => g?.edges || [];

/** Rótulo curto: sem prefixo de pacote, sem extensão, no máximo 30 caracteres. */
export const rotulo = (n: NoBruto): string =>
  String(n.label || n.node_key).replace(/^pkg:/, '').replace(/\.(ts|js|py|java)$/, '').slice(0, 30);

export function graus(es: Aresta[]): Map<string, number> {
  const d = new Map<string, number>();
  for (const e of es) {
    d.set(e.src, (d.get(e.src) || 0) + 1);
    d.set(e.dst, (d.get(e.dst) || 0) + 1);
  }
  return d;
}

export function raioDe(n: NoBruto, grau: number): number {
  const base = n.kind === 'file' ? 11
    : (n.kind === 'controller' || n.kind === 'service' || n.kind === 'module') ? 10
      : n.kind === 'endpoint' ? 7
        : n.kind === 'package' ? 6 : 8;
  return base + Math.min(7, Math.sqrt(grau) * 1.5);
}

/** Tipos presentes, ordenados pela banda: a legenda desce junto com o desenho. */
export const tiposDoGrafo = (g: GrafoBruto | null): string[] =>
  [...new Set(nos(g).map((n) => n.kind))].sort((a, b) => (BAND[a] ?? 9) - (BAND[b] ?? 9));

/**
 * Tipos ligados por padrão.
 *
 * No modo código, método e função ficam de fora: num projeto Nest eles são
 * milhares e afogam o desenho. Nos outros modos tudo entra — a memória do time é
 * pequena por natureza, e esconder parte dela seria esconder o assunto.
 */
export const tiposPadrao = (g: GrafoBruto | null, modo: Modo): Set<string> => {
  const todos = [...new Set(nos(g).map((n) => n.kind))];
  return new Set(modo === 'codigo' ? todos.filter((k) => DEFAULT_KINDS.includes(k)) : todos);
};

export interface Filtros {
  tipos: Set<string>;
  relacoes: Set<string>;
  esconderIsolados: boolean;
  profundidade: number;
}

/**
 * O dev mexeu em algum controle?
 *
 * Serve para o botão "limpar filtros" só aparecer quando há o que limpar: o
 * estado inicial já esconde método e função de propósito, e avisar sobre isso
 * todo dia seria ruído.
 */
export function estaRecortado(g: GrafoBruto | null, f: Filtros, modo: Modo): boolean {
  if (!g) return false;
  const padrao = tiposPadrao(g, modo);
  const todasRel = new Set(arestas(g).map((e) => e.rel));
  return f.tipos.size !== padrao.size
    || [...padrao].some((k) => !f.tipos.has(k))
    || f.relacoes.size !== todasRel.size
    || f.esconderIsolados
    || f.profundidade > 0;
}

export function contarPorTipo(g: GrafoBruto | null): Record<string, number> {
  const c: Record<string, number> = {};
  for (const n of nos(g)) c[n.kind] = (c[n.kind] || 0) + 1;
  return c;
}

export function contarPorRelacao(g: GrafoBruto | null): Record<string, number> {
  const c: Record<string, number> = {};
  for (const e of arestas(g)) c[e.rel] = (c[e.rel] || 0) + 1;
  return c;
}

/**
 * Aplica os filtros e produz os nós que a física move.
 *
 * `anteriores` preserva posição, fixação e o `seeded` de quem já estava na tela:
 * ligar um tipo de nó não pode reorganizar o grafo inteiro debaixo do cursor de
 * quem estava lendo.
 */
export function filtrar(
  g: GrafoBruto | null,
  f: Filtros,
  anteriores: No[] | null,
): { nos: No[]; arestas: Aresta[]; mapa: Map<string, No> } {
  if (!g) return { nos: [], arestas: [], mapa: new Map() };
  const guardados = new Map((anteriores || []).map((n) => [n.node_key, n]));
  const grau = graus(arestas(g));

  let lista = nos(g).filter((n) => f.tipos.has(n.kind));
  let chaves = new Set(lista.map((n) => n.node_key));
  let es = arestas(g).filter((e) => f.relacoes.has(e.rel) && chaves.has(e.src) && chaves.has(e.dst));

  if (f.esconderIsolados) {
    const ligados = new Set<string>();
    for (const e of es) { ligados.add(e.src); ligados.add(e.dst); }
    lista = lista.filter((n) => ligados.has(n.node_key));
    chaves = new Set(lista.map((n) => n.node_key));
    es = es.filter((e) => chaves.has(e.src) && chaves.has(e.dst));
  }

  const resultado: No[] = lista.map((n) => {
    const velho = guardados.get(n.node_key);
    const r = raioDe(n, grau.get(n.node_key) || 0);
    return {
      ...n,
      r,
      lw: Math.max(r * 2 + 22, rotulo(n).length * 6.4 + 16),
      x: velho ? velho.x : 0,
      y: velho ? velho.y : 0,
      vx: 0,
      vy: 0,
      pinned: velho ? velho.pinned : false,
      seeded: !!velho,
    };
  });
  return { nos: resultado, arestas: es, mapa: new Map(resultado.map((n) => [n.node_key, n])) };
}

/**
 * Só as bandas em uso ocupam altura.
 *
 * Sem isso um grafo de 3 tipos ficaria espalhado numa tela alta e o ajuste
 * automático deixaria tudo ilegível.
 */
export function rankDeBandas(lista: No[]): Map<number, number> {
  const usadas = [...new Set(lista.map((n) => BAND[n.kind] ?? 5))].sort((a, b) => a - b);
  return new Map(usadas.map((b, i) => [b, i]));
}

export const yDaBanda = (n: No, rank: Map<number, number>): number =>
  (rank.get(BAND[n.kind] ?? 5) ?? 0) * ALTURA_DA_BANDA;

/** Posição inicial determinística: em camadas por tipo, ou em espiral na força. */
export function semear(lista: No[], layout: Layout, rank: Map<number, number>): void {
  if (layout === 'layers') {
    const bandas = new Map<number, No[]>();
    for (const n of lista) {
      const b = BAND[n.kind] ?? 5;
      if (!bandas.has(b)) bandas.set(b, []);
      bandas.get(b)!.push(n);
    }
    for (const fila of bandas.values()) {
      fila.sort((a, c) => (a.file || '').localeCompare(c.file || '') || a.node_key.localeCompare(c.node_key));
      const total = fila.reduce((s, n) => s + n.lw, 0);
      let x = -total / 2;
      for (const n of fila) {
        n.y = yDaBanda(n, rank);
        n.x = x + n.lw / 2;
        x += n.lw;
        n.vx = 0; n.vy = 0; n.seeded = true;
      }
    }
    return;
  }
  lista.forEach((n, i) => {
    const a = i * 2.399963;
    const r = 26 * Math.sqrt(i + 1);
    n.x = Math.cos(a) * r * 1.6;
    n.y = Math.sin(a) * r * 1.6;
    n.vx = 0; n.vy = 0; n.seeded = true;
  });
}

export interface Fisica {
  nos: No[];
  arestas: Aresta[];
  mapa: Map<string, No>;
  rank: Map<number, number>;
  layout: Layout;
  alpha: number;
  arrastando: No | null;
}

export function passo(s: Fisica): void {
  if (!s.nos.length) return;
  if (s.layout === 'layers') return passoEmCamadas(s);

  const cel = 110;
  const grade = new Map<string, No[]>();
  const chave = (x: number, y: number) => `${(x / cel) | 0}:${(y / cel) | 0}`;
  for (const n of s.nos) {
    const k = chave(n.x, n.y);
    if (!grade.has(k)) grade.set(k, []);
    grade.get(k)!.push(n);
  }
  // repulsão só entre vizinhos de célula: aguenta grafo grande sem travar
  for (const n of s.nos) {
    const cx = (n.x / cel) | 0;
    const cy = (n.y / cel) | 0;
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        for (const m of grade.get(`${cx + i}:${cy + j}`) || []) {
          if (m === n) continue;
          const dx = n.x - m.x;
          const dy = n.y - m.y;
          const d2 = dx * dx + dy * dy || 0.01;
          if (d2 > cel * cel * 4) continue;
          const f = (1400 + n.r * m.r * 4) / d2;
          n.vx += (dx / Math.sqrt(d2)) * f * s.alpha;
          n.vy += (dy / Math.sqrt(d2)) * f * s.alpha;
        }
      }
    }
  }
  for (const e of s.arestas) {
    const a = s.mapa.get(e.src);
    const b = s.mapa.get(e.dst);
    if (!a || !b) continue;
    const quer = e.rel === 'defines' || e.rel === 'exposes' ? 70 : e.rel === 'injects' ? 110 : 130;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const d = Math.hypot(dx, dy) || 0.01;
    const k = (d - quer) * 0.012 * s.alpha;
    a.vx += (dx / d) * k; a.vy += (dy / d) * k;
    b.vx -= (dx / d) * k; b.vy -= (dy / d) * k;
  }
  for (const n of s.nos) {
    n.vx += (0 - n.x) * 0.0022;
    n.vy += (0 - n.y) * 0.0022;
    if (n !== s.arrastando && !n.pinned) {
      n.x += Math.max(-18, Math.min(18, n.vx));
      n.y += Math.max(-18, Math.min(18, n.vy));
    }
    n.vx *= 0.78; n.vy *= 0.78;
  }
}

/**
 * Camadas: y travado na banda do tipo; em x cada nó busca a média dos vizinhos
 * (fica embaixo de quem o usa) e a banda garante um vão mínimo entre nós — mola
 * por distância aqui colapsaria tudo numa coluna.
 */
function passoEmCamadas(s: Fisica): void {
  const soma = new Map<No, number>();
  const conta = new Map<No, number>();
  for (const e of s.arestas) {
    const a = s.mapa.get(e.src);
    const b = s.mapa.get(e.dst);
    if (!a || !b) continue;
    soma.set(a, (soma.get(a) || 0) + b.x); conta.set(a, (conta.get(a) || 0) + 1);
    soma.set(b, (soma.get(b) || 0) + a.x); conta.set(b, (conta.get(b) || 0) + 1);
  }
  for (const n of s.nos) {
    const c = conta.get(n);
    // esfria: no fim o vão mínimo manda
    if (c) n.vx += ((soma.get(n)! / c) - n.x) * 0.07 * Math.max(0.04, s.alpha);
    n.vy += (yDaBanda(n, s.rank) - n.y) * 0.30;
  }
  for (const n of s.nos) {
    if (n !== s.arrastando && !n.pinned) {
      n.x += Math.max(-22, Math.min(22, n.vx));
      n.y += Math.max(-22, Math.min(22, n.vy));
    }
    n.vx *= 0.62; n.vy *= 0.62;
  }

  // o vão mínimo é restrição, não força: resolvido na posição, cabe sempre o rótulo
  const bandas = new Map<number, No[]>();
  for (const n of s.nos) {
    const b = BAND[n.kind] ?? 5;
    if (!bandas.has(b)) bandas.set(b, []);
    bandas.get(b)!.push(n);
  }
  const livre = (n: No) => n !== s.arrastando && !n.pinned;
  for (let volta = 0; volta < 3; volta++) {
    for (const fila of bandas.values()) {
      fila.sort((a, b) => a.x - b.x);
      for (let i = 1; i < fila.length; i++) {
        const p = fila[i - 1];
        const c = fila[i];
        const vao = (p.lw + c.lw) / 2;
        const d = c.x - p.x;
        if (d >= vao) continue;
        const m = (vao - d) / 2;
        if (livre(p) && livre(c)) { p.x -= m; c.x += m; }
        else if (livre(c)) c.x += m * 2;
        else if (livre(p)) p.x -= m * 2;
      }
    }
  }
  for (const fila of bandas.values()) {
    let media = 0;
    let k = 0;
    for (const n of fila) if (livre(n)) { media += n.x; k++; }
    if (!k) continue;
    media /= k;
    for (const n of fila) if (livre(n)) n.x -= media * 0.06; // volta devagar para o centro
  }
}

/* ---------------------------------------------------------------- viewport */

export const paraTela = (p: Ponto, v: Camera, w: number, h: number): Ponto =>
  ({ x: (p.x - v.x) * v.k + w / 2, y: (p.y - v.y) * v.k + h / 2 });

export const paraMundo = (p: Ponto, v: Camera, w: number, h: number): Ponto =>
  ({ x: (p.x - w / 2) / v.k + v.x, y: (p.y - h / 2) / v.k + v.y });

/** Câmera que enquadra a lista; null quando não há o que enquadrar. */
export function ajustar(lista: No[], w: number, h: number): Camera | null {
  if (!lista.length || !w || !h) return null; // aba escondida ainda não tem tamanho
  let x0 = 1e9; let y0 = 1e9; let x1 = -1e9; let y1 = -1e9;
  for (const n of lista) {
    x0 = Math.min(x0, n.x - n.r); y0 = Math.min(y0, n.y - n.r);
    x1 = Math.max(x1, n.x + n.r); y1 = Math.max(y1, n.y + n.r);
  }
  const pad = 70;
  return {
    k: Math.max(0.12, Math.min(2.2, Math.min(
      (w - pad * 2) / Math.max(1, x1 - x0),
      (h - pad * 2) / Math.max(1, y1 - y0),
    ))),
    x: (x0 + x1) / 2,
    y: (y0 + y1) / 2,
  };
}

export function escolher(lista: No[], tela: Ponto, v: Camera, w: number, h: number): No | null {
  const p = paraMundo(tela, v, w, h);
  let melhor: No | null = null;
  let dist = 1e9;
  for (const n of lista) {
    const d = Math.hypot(n.x - p.x, n.y - p.y);
    if (d < n.r + 8 / v.k && d < dist) { dist = d; melhor = n; }
  }
  return melhor;
}

/* ------------------------------------------------------- foco e vizinhança */

/** Vizinhança até `profundidade` saltos; null quer dizer "mostra tudo". */
export function vizinhanca(es: Aresta[], chave: string, profundidade: number): Set<string> | null {
  const set = new Set([chave]);
  if (!profundidade) return null;
  let fronteira = [chave];
  for (let d = 0; d < profundidade; d++) {
    const proxima: string[] = [];
    for (const e of es) {
      if (fronteira.includes(e.src) && !set.has(e.dst)) { set.add(e.dst); proxima.push(e.dst); }
      if (fronteira.includes(e.dst) && !set.has(e.src)) { set.add(e.src); proxima.push(e.src); }
    }
    fronteira = proxima;
    if (!proxima.length) break;
  }
  return set;
}

export const saoVizinhos = (es: Aresta[], a: No, b: No): boolean =>
  es.some((e) => (e.src === a.node_key && e.dst === b.node_key)
    || (e.src === b.node_key && e.dst === a.node_key));

export function casaComBusca(n: NoBruto, q: string): boolean {
  if (!q) return false;
  const t = q.toLowerCase();
  return (n.node_key || '').toLowerCase().includes(t)
    || (n.label || '').toLowerCase().includes(t)
    || (n.file || '').toLowerCase().includes(t);
}

export const isolados = (lista: No[], es: Aresta[]): No[] =>
  lista.filter((n) => !es.some((e) => e.src === n.node_key || e.dst === n.node_key));

/* -------------------------------------------------- painel lateral em texto */

export interface ItemLateral { no: NoBruto; extra?: string }

/** Rotas, mais dependidos e arquivos sem relação — a leitura em texto do grafo. */
export function lateralDeCodigo(g: GrafoBruto | null) {
  const rotas = nos(g).filter((n) => n.kind === 'endpoint');
  const entrada = new Map<string, number>();
  for (const e of arestas(g)) entrada.set(e.dst, (entrada.get(e.dst) || 0) + 1);
  const hubs = nos(g)
    .filter((n) => n.kind !== 'package')
    .map((n) => ({ no: n, d: entrada.get(n.node_key) || 0 }))
    .filter((x) => x.d > 0)
    .sort((a, b) => b.d - a.d)
    .slice(0, 8);
  const ligados = new Set<string>();
  for (const e of arestas(g)) { ligados.add(e.src); ligados.add(e.dst); }
  const orfaos = nos(g).filter((n) => n.kind === 'file' && !ligados.has(n.node_key)).slice(0, 10);
  return {
    rotas: rotas.slice(0, 14).map((n): ItemLateral => ({ no: n, extra: n.file ? n.file.split('/').pop() : '' })),
    totalRotas: rotas.length,
    hubs: hubs.map((x): ItemLateral => ({ no: x.no, extra: `${x.d}×` })),
    orfaos: orfaos.map((n): ItemLateral => ({ no: n })),
  };
}

export function lateralDeMemoria(g: GrafoBruto | null, totais: GrafoBruto['totals']) {
  const porTipo = (k: string) => nos(g).filter((n) => n.kind === k);
  return {
    arquivosComMemoria: totais?.files_with_memory || 0,
    arquivos: totais?.files || 0,
    aConfirmar: nos(g).filter((n) => n.review_state === 'nao_confirmada').slice(0, 10).map((n): ItemLateral => ({ no: n })),
    totalAConfirmar: nos(g).filter((n) => n.review_state === 'nao_confirmada').length,
    deriva: nos(g).filter((n) => n.drift).slice(0, 10).map((n): ItemLateral => ({ no: n, extra: 'código sumiu' })),
    totalDeriva: nos(g).filter((n) => n.drift).length,
    lacunas: porTipo('lacuna').slice(0, 10).map((n): ItemLateral => ({ no: n })),
    totalLacunas: porTipo('lacuna').length,
  };
}

export function lateralDeCobertura(g: GrafoBruto | null) {
  const arquivos = nos(g).filter((n) => n.kind === 'file');
  const vazios = arquivos.filter((n) => !n.memory);
  const ricos = arquivos.filter((n) => n.memory).sort((a, b) => (b.memory || 0) - (a.memory || 0));
  return {
    pct: arquivos.length ? Math.round(((arquivos.length - vazios.length) / arquivos.length) * 100) : 0,
    vazios: vazios.slice(0, 16).map((n): ItemLateral => ({ no: n })),
    totalVazios: vazios.length,
    ricos: ricos.slice(0, 10).map((n): ItemLateral => ({ no: n, extra: `${n.memory}×` })),
  };
}

/**
 * Modo cobertura: o grafo de código carimbado com quantos artefatos de memória
 * tocam cada arquivo. Arquivo em zero fica apagado, e é esse o ponto do modo.
 */
export function comCobertura(codigo: GrafoBruto | null, porArquivo: Map<string, number>): GrafoBruto {
  return {
    nodes: nos(codigo).map((n) => ({
      ...n,
      memory: n.file ? (porArquivo.get(n.file) || 0) : (porArquivo.get(n.node_key) || 0),
    })),
    edges: arestas(codigo),
  };
}

/** Mesma semântica do servidor (knowledge.js): `**` cruza diretório, `*` não. */
export function casaGlob(padrao: string, caminho: string): boolean {
  const pat = String(padrao || '').replace(/^\.\//, '');
  let re = '';
  for (let i = 0; i < pat.length; i++) {
    const c = pat[i];
    if (c === '*') {
      if (pat[i + 1] === '*') {
        if (pat[i + 2] === '/') { re += '(?:.*/)?'; i += 2; } else { re += '.*'; i += 1; }
      } else re += '[^/]*';
    } else if (c === '?') re += '[^/]';
    else if ('\\^$.|+()[]{}'.includes(c)) re += `\\${c}`;
    else re += c;
  }
  if (pat.endsWith('/')) re += '.*';
  try {
    return new RegExp(`^${re}$`).test(String(caminho || '').replace(/\\/g, '/'));
  } catch {
    return false;
  }
}

export interface Dono {
  /** ausente na resposta pública do grafo; a aba Saúde precisa dele para remover */
  id?: string | number;
  pattern: string;
  owner_email: string;
  role?: string | null;
}

/** Quem responde pelo arquivo: `owner` na frente, senão o primeiro que casou. */
export function donoDe(donos: Dono[], chave: string): string | null {
  const casos = (donos || []).filter((o) => casaGlob(o.pattern, chave));
  const dono = casos.find((o) => o.role === 'owner') || casos[0];
  return dono ? dono.owner_email : null;
}
